import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/Industries.css';

const Industries = () => {
  useEffect(() => {
    document.title = 'Industries We Serve - Pentagram Infotech';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      'Pentagram Infotech serves diverse industries including healthcare, finance, education, retail, manufacturing, and government sectors with tailored IT solutions.'
    );
  }, []);

  const industries = [
    {
      id: 1,
      title: 'Healthcare',
      description: 'Secure and compliant IT solutions for healthcare providers',
      icon: 'bi-hospital',
      color: '#3b82f6',
      features: ['HIPAA Compliance', 'Electronic Health Records', 'Telemedicine Solutions', 'Healthcare Analytics'],
      solutions: ['Secure Data Management', 'Compliance Solutions', 'Telehealth Platforms']
    },
    {
      id: 2,
      title: 'Finance & Banking',
      description: 'Robust financial technology solutions for banks and financial institutions',
      icon: 'bi-bank',
      color: '#0e9f6e',
      features: ['PCI DSS Compliance', 'Fraud Detection', 'Digital Banking', 'Risk Management'],
      solutions: ['Core Banking Systems', 'Payment Gateways', 'Financial Analytics']
    },
    {
      id: 3,
      title: 'Education',
      description: 'Technology solutions for educational institutions and e-learning platforms',
      icon: 'bi-mortarboard',
      color: '#8b5cf6',
      features: ['LMS Integration', 'Virtual Classrooms', 'Student Management Systems', 'E-learning Platforms'],
      solutions: ['Online Learning Platforms', 'Campus Management Systems', 'Educational Apps']
    },
    {
      id: 4,
      title: 'Retail & E-commerce',
      description: 'Digital solutions for retail businesses and e-commerce platforms',
      icon: 'bi-cart',
      color: '#f59e0b',
      features: ['Inventory Management', 'E-commerce Platforms', 'Customer Analytics', 'POS Systems'],
      solutions: ['E-commerce Websites', 'Inventory Systems', 'Customer Relationship Management']
    },
    {
      id: 5,
      title: 'Manufacturing',
      description: 'Industry 4.0 solutions for manufacturing and production',
      icon: 'bi-gear',
      color: '#ef4444',
      features: ['IoT Integration', 'Supply Chain Management', 'Quality Control Systems', 'Production Analytics'],
      solutions: ['ERP Systems', 'Supply Chain Solutions', 'Production Monitoring']
    },
    {
      id: 6,
      title: 'Government',
      description: 'Secure IT solutions for government agencies and public sector',
      icon: 'bi-building',
      color: '#1a56db',
      features: ['Security Compliance', 'Digital Governance', 'Public Service Platforms', 'Data Security'],
      solutions: ['Government Portals', 'Secure Communication', 'Public Service Systems']
    },
    {
      id: 7,
      title: 'Technology',
      description: 'Solutions for tech companies and startups',
      icon: 'bi-cpu',
      color: '#ec4899',
      features: ['Cloud Infrastructure', 'DevOps Solutions', 'Software Development', 'Technical Support'],
      solutions: ['SaaS Platforms', 'Development Services', 'Technical Consulting']
    },
    {
      id: 8,
      title: 'Hospitality',
      description: 'Technology solutions for hotels and hospitality businesses',
      icon: 'bi-house-door',
      color: '#10b981',
      features: ['Property Management', 'Booking Systems', 'Customer Service', 'Revenue Management'],
      solutions: ['Hotel Management Systems', 'Online Booking Platforms', 'Customer Service Solutions']
    }
  ];

  return (
    <div className="industries-page page-transition">
      {/* Hero Banner */}
      <section className="industries-hero">
        <div className="container">
          <div className="row align-items-center min-vh-60">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="page-title">Industries We Serve</h1>
              <p className="page-subtitle">
                We provide tailored IT solutions for diverse industries, helping businesses 
                leverage technology for growth, efficiency, and competitive advantage.
              </p>
              <div className="hero-stats">
                <div className="stat">
                  <span className="number">8+</span>
                  <span className="label">Industries</span>
                </div>
                <div className="stat">
                  <span className="number">25+</span>
                  <span className="label">Years Experience</span>
                </div>
                <div className="stat">
                  <span className="number">500+</span>
                  <span className="label">Projects</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="industries-grid section-padding">
        <div className="container">
          <div className="section-header text-center mb-5">
            <span className="section-label">Our Expertise</span>
            <h2 className="section-title">Industry-Specific Solutions</h2>
            <p className="section-subtitle">
              We understand the unique challenges of each industry and deliver customized solutions.
            </p>
          </div>

          <div className="row g-4">
            {industries.map((industry) => (
              <div className="col-lg-6" key={industry.id}>
                <div 
                  className="industry-card"
                  style={{ '--industry-color': industry.color }}
                >
                  <div className="industry-header">
                    <div className="industry-icon">
                      <i className={`bi ${industry.icon}`}></i>
                    </div>
                    <div className="industry-title">
                      <h3>{industry.title}</h3>
                      <p>{industry.description}</p>
                    </div>
                  </div>
                  
                  <div className="industry-features">
                    <h4>Key Features</h4>
                    <div className="features-grid">
                      {industry.features.map((feature, idx) => (
                        <span className="feature-tag" key={idx}>
                          <i className="bi bi-check"></i>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="industry-solutions">
                    <h4>Our Solutions</h4>
                    <ul>
                      {industry.solutions.map((solution, idx) => (
                        <li key={idx}>
                          <i className="bi bi-arrow-right"></i>
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="industry-cta">
                    <Link to={`/services?industry=${industry.title.toLowerCase()}`} className="btn btn-outline-primary">
                      Explore Solutions
                      <i className="bi bi-arrow-right ms-2"></i>
                    </Link>
                    <Link to="/contact" className="btn btn-primary">
                      Get Consultation
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="industry-cta-section text-center mt-5">
            <div className="cta-content">
              <h3>Looking for Industry-Specific Solutions?</h3>
              <p>Contact us to discuss how we can help your industry with tailored technology solutions.</p>
              <Link to="/contact" className="btn btn-primary btn-lg">
                Get Industry Consultation
                <i className="bi bi-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="tech-stack section-padding bg-light">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="section-title">Technology Stack</h2>
            <p className="section-subtitle">
              We leverage cutting-edge technologies to deliver industry-specific solutions
            </p>
          </div>
          
          <div className="tech-grid">
            <div className="tech-category">
              <h4>Cloud Platforms</h4>
              <div className="tech-tags">
                <span className="tech-tag">AWS</span>
                <span className="tech-tag">Azure</span>
                <span className="tech-tag">Google Cloud</span>
                <span className="tech-tag">IBM Cloud</span>
              </div>
            </div>
            
            <div className="tech-category">
              <h4>Development</h4>
              <div className="tech-tags">
                <span className="tech-tag">React</span>
                <span className="tech-tag">Node.js</span>
                <span className="tech-tag">Python</span>
                <span className="tech-tag">Java</span>
                <span className="tech-tag">.NET</span>
              </div>
            </div>
            
            <div className="tech-category">
              <h4>Security</h4>
              <div className="tech-tags">
                <span className="tech-tag">SIEM</span>
                <span className="tech-tag">Firewalls</span>
                <span className="tech-tag">Encryption</span>
                <span className="tech-tag">VPN</span>
              </div>
            </div>
            
            <div className="tech-category">
              <h4>Database</h4>
              <div className="tech-tags">
                <span className="tech-tag">MySQL</span>
                <span className="tech-tag">PostgreSQL</span>
                <span className="tech-tag">MongoDB</span>
                <span className="tech-tag">Oracle</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;